import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import { BrowserRouter } from 'react-router-dom'
import './index.css'
import { createLogger } from './lib/logging'
import { initCaptionSync } from './lib/caption-sync'
import { AuthProvider } from './contexts/AuthContext'
import { registerPushInBackground } from './services/push'
import { registerSW } from 'virtual:pwa-register'
import { APP_VERSION } from './config/appConfig'
import { initUpdateManager } from './services/updateManager'
import { checkStorageBucket, PROFILE_AVATAR_BUCKET } from './lib/supabase'

import { setupErrorTraps } from './lib/error-trap'

const PERF_DEBUG_SESSION_ID = "slow-page-load"
const PWA_DEBUG_SESSION_ID = "pwa-non-precached"

function reportPerfDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PERF_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const perfDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!perfDebugUrl) return
  fetch(perfDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PERF_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

function reportPwaDebug(hypothesisId: string, location: string, msg: string, data: Record<string, unknown>) {
  if (!(import.meta as any)?.env?.DEV) return
  if (typeof window === "undefined" || (window as any).__TRAE_PWA_DEBUG__ !== true) return
  if (typeof fetch !== "function") return
  const pwaDebugUrl = String((window as any).__TRAE_LOCAL_DEBUG_URL__ || "").trim()
  if (!pwaDebugUrl) return
  fetch(pwaDebugUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId: PWA_DEBUG_SESSION_ID,
      runId: "pre-fix",
      hypothesisId,
      location,
      msg,
      data,
      ts: Date.now(),
    }),
  }).catch(() => { })
}

const bootStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()

// Initialize comprehensive error handling immediately
setupErrorTraps()

if (typeof window !== "undefined") {
  ; (window as any).__slowPageLoadBootStart = bootStartMs
  // #region debug-point A:main-bootstrap-start
  reportPerfDebug("A", "src/main.tsx:31", "[DEBUG] Main bootstrap started", {
    bootStartMs,
    path: window.location.pathname,
    isDev: Boolean((import.meta as any)?.env?.DEV),
  })
  // #endregion
}

// Initialize structured logger and patch console
const logger = createLogger()
logger.info('App version', { category: 'app', meta: { version: APP_VERSION } })

if (typeof window !== 'undefined') {
  const NETWORK_ONLY_UNTIL_KEY = 'pwa_network_only_until'
  const RELOADS_KEY = 'app_reload_times'
  const ROLLBACK_DONE_KEY = 'pwa_rollback_done'
  const LAST_WORKING_VERSION_KEY = 'last_working_version'

  const isNetworkOnlyMode = () => {
    try {
      const until = Number(localStorage.getItem(NETWORK_ONLY_UNTIL_KEY) || '0')
      return Number.isFinite(until) && until > Date.now()
    } catch {
      return false
    }
  }

  const enterNetworkOnlyMode = (durationMs: number) => {
    try {
      localStorage.setItem(NETWORK_ONLY_UNTIL_KEY, String(Date.now() + durationMs))
    } catch {
    }
  }

  const clearCachesAndUnregisterSw = async () => {
    const cacheDeleteStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
    try {
      if ('caches' in window) {
        const keys = await caches.keys()
        const appKeys = keys.filter((k) => k.includes('workbox') || k.includes('html-cache') || k.includes('assets-cache') || k.includes('images-cache') || k.includes('precache'))
        await Promise.all(appKeys.map((k) => caches.delete(k)))
        // #region debug-point C:pwa-cache-clear
        reportPwaDebug("C", "src/main.tsx:102", "[DEBUG] Cleared caches during SW reset", {
          keys: appKeys,
          elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - cacheDeleteStartMs,
        })
        // #endregion
      }
    } catch {
    }

    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations()
        const appRegs = regs.filter((r) => {
          try {
            return new URL(r.scope).origin === window.location.origin
          } catch {
            return false
          }
        })
        await Promise.all(appRegs.map((r) => r.unregister()))
        // #region debug-point C:pwa-sw-unregister
        reportPwaDebug("C", "src/main.tsx:114", "[DEBUG] Unregistered service workers during reset", {
          registrationCount: appRegs.length,
          scopes: appRegs.map((r) => r.scope),
        })
        // #endregion
      }
    } catch {
    }
  }

  try {
    const isDev = Boolean((import.meta as any)?.env?.DEV)
    const host = String(window.location.hostname || "")
    const onceKey = "__dev_sw_cleanup_done__"
    if (isDev && (host === "localhost" || host === "127.0.0.1") && sessionStorage.getItem(onceKey) !== "true") {
      sessionStorage.setItem(onceKey, "true")
      void clearCachesAndUnregisterSw()
    }
  } catch {
  }

  const detectCrashReloadLoop = () => {
    try {
      const already = sessionStorage.getItem(ROLLBACK_DONE_KEY) === 'true'
      if (already) return false
      const now = Date.now()
      const raw = sessionStorage.getItem(RELOADS_KEY) || '[]'
      const arr = (JSON.parse(raw) as number[]).filter((t) => Number.isFinite(t) && now - t < 10_000)
      arr.push(now)
      sessionStorage.setItem(RELOADS_KEY, JSON.stringify(arr))
      if (arr.length >= 3) {
        sessionStorage.setItem(ROLLBACK_DONE_KEY, 'true')
        return true
      }
      return false
    } catch {
      return false
    }
  }

  const inLoop = detectCrashReloadLoop()
  if (inLoop) {
    // #region debug-point D:pwa-crash-loop
    reportPwaDebug("D", "src/main.tsx:151", "[DEBUG] Crash reload loop detected", {
      version: APP_VERSION,
    })
    // #endregion
    console.error('[rollback] Detected repeated reloads. Disabling PWA caches and service worker.')
    logger.error('Rollback triggered: repeated reloads', { category: 'app', meta: { version: APP_VERSION } })
    enterNetworkOnlyMode(30 * 60 * 1000)
    void clearCachesAndUnregisterSw().finally(() => {
      try {
        window.location.reload()
      } catch {
      }
    })
  } else {
    try {
      window.setTimeout(() => {
        localStorage.setItem(LAST_WORKING_VERSION_KEY, APP_VERSION)
      }, 10_000)
    } catch {
    }
  }

  const w = window as any
  if (!w.__PWA_SW_REGISTERED__) {
    w.__PWA_SW_REGISTERED__ = true
    if (!isNetworkOnlyMode()) {
      const isDev = Boolean((import.meta as any)?.env?.DEV)
      if (!isDev) {
        const updateSW = registerSW({
          immediate: true,
          onRegistered(registration) {
            // #region debug-point A:pwa-sw-registered
            reportPwaDebug("A", "src/main.tsx:177", "[DEBUG] PWA SW registered", {
              swUrl: registration?.active?.scriptURL || registration?.installing?.scriptURL || registration?.waiting?.scriptURL || null,
              scope: registration?.scope || null,
              hasInstalling: Boolean(registration?.installing),
              hasWaiting: Boolean(registration?.waiting),
              hasActive: Boolean(registration?.active),
            })
            // #endregion
          },
          onRegisterError(error) {
            // #region debug-point A:pwa-sw-register-error
            reportPwaDebug("A", "src/main.tsx:187", "[DEBUG] PWA SW registration failed", {
              message: error instanceof Error ? error.message : String(error),
            })
            // #endregion
          },
          onNeedRefresh() {
            // #region debug-point B:pwa-need-refresh
            reportPwaDebug("B", "src/main.tsx:194", "[DEBUG] PWA reported needRefresh", {
              version: APP_VERSION,
            })
            // #endregion
            window.dispatchEvent(new Event('pwa-update-available'))
          },
          onOfflineReady() {
            // #region debug-point B:pwa-offline-ready
            reportPwaDebug("B", "src/main.tsx:201", "[DEBUG] PWA offline ready", {
              version: APP_VERSION,
            })
            // #endregion
            console.log('Offline ready')
          },
        })
        w.__PWA_UPDATE_SW__ = updateSW
      } else {
        w.__PWA_UPDATE_SW__ = null
      }
    } else {
      w.__PWA_UPDATE_SW__ = null
    }
  }
}

initUpdateManager({ logger })

// Initialize caption synchronization to react to language changes and dynamic content
initCaptionSync()

// Proactive Supabase Storage health check (runs at startup, throttled)
if (typeof window !== 'undefined') {
  try {
    const key = 'storage_bucket_health_checked_at'
    const last = Number(localStorage.getItem(key) || '0')
    const now = Date.now()
    const dayMs = 24 * 60 * 60 * 1000
    if (!Number.isFinite(last) || now - last > dayMs) {
      localStorage.setItem(key, String(now))
      const storageCheckStartMs = typeof performance !== "undefined" ? performance.now() : Date.now()
      void checkStorageBucket(PROFILE_AVATAR_BUCKET).then((res) => {
        // #region debug-point C:storage-health-check
        reportPerfDebug("C", "src/main.tsx:176", "[DEBUG] Storage health check completed", {
          bucket: PROFILE_AVATAR_BUCKET,
          ok: res.ok,
          kind: res.kind || null,
          elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - storageCheckStartMs,
        })
        // #endregion
        if (!res.ok) {
          console.error('[storage] bucket health check failed', res)
          logger.error('Storage bucket health check failed', { category: 'app', meta: res })
        } else {
          logger.info('Storage bucket health check ok', { category: 'app', meta: res })
        }
      })
    }
  } catch {
  }
}

// Background web-push registration (no UI change)
registerPushInBackground().catch(() => { })

// #region debug-point A:main-render-dispatch
reportPerfDebug("A", "src/main.tsx:198", "[DEBUG] Root render dispatched", {
  elapsedMs: (typeof performance !== "undefined" ? performance.now() : Date.now()) - bootStartMs,
})
// #endregion

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
)
